import { z } from 'zod';
import { partnerSlug } from './slug.ts';

/**
 * The eight channels, thirteen placements and eight destinations from the Lists tab of
 * sparkery-competition-partner-links.xlsx. Seeded as reference data; admin-editable later.
 */
export const CHANNELS = [
  'partner',
  'influencer',
  'affiliate',
  'email',
  'social',
  'print',
  'podcast',
  'event',
] as const;

export const PLACEMENTS = [
  'instagram-post',
  'instagram-story',
  'instagram-reel',
  'facebook',
  'tiktok',
  'youtube',
  'newsletter',
  'blog-post',
  'website-banner',
  'podcast-episode',
  'print-ad',
  'qr-code',
  'other',
] as const;

export const DESTINATIONS = [
  { label: 'Competition page', url: 'https://www.sparkery.com/competition' },
  { label: 'Homepage', url: 'https://www.sparkery.com/' },
  { label: 'Pricing', url: 'https://www.sparkery.com/pricing' },
  { label: 'Wearable', url: 'https://www.sparkery.com/wearable' },
  { label: 'Smart Scale', url: 'https://www.sparkery.com/smart-scale' },
  { label: 'Health Hub', url: 'https://www.sparkery.com/hub' },
  { label: 'Get started', url: 'https://www.sparkery.com/qr-get-started' },
  { label: 'Bundle', url: 'https://www.sparkery.com/body-bundle' },
] as const;

export const channelSchema = z.enum(CHANNELS);
export type Channel = z.infer<typeof channelSchema>;
export const placementSchema = z.enum(PLACEMENTS);

/** The three values on the spreadsheet's Settings tab. */
export const settingsSchema = z.object({
  campaign: z.string().min(1),
  fallbackDestination: z.string().url(),
  defaultChannel: channelSchema,
});
export type Settings = z.infer<typeof settingsSchema>;

export const referralLinkInputSchema = z.object({
  partnerName: z.string().min(1),
  label: z.string().min(1),
  destination: z.string().url().optional(),
  channel: channelSchema.optional(),
  placement: placementSchema.optional(),
  campaign: z.string().min(1).optional(),
});
export type ReferralLinkInput = z.infer<typeof referralLinkInputSchema>;

/**
 * The programme-wide settings, as the admin screen edits them.
 *
 * A superset of `settingsSchema` above: those three feed the link generator, and these
 * two feed the commission domain. One screen, because they are all one negotiation —
 * but note they behave very differently, which is why the commission pair carry
 * warnings of their own.
 */
export const programmeSettingsSchema = settingsSchema.extend({
  /**
   * Days from an order's processed date before its commission is payable.
   *
   * Read at accrual time and STORED on the entry, so a change never moves money that
   * has already accrued — it reaches only what the next scrape accrues.
   */
  commissionMaturityDays: z.number().int().min(1).max(365),
  /**
   * The programme's opening date, `YYYY-MM-DD`.
   *
   * The dangerous one. Moving it EARLIER lets a re-scrape accrue commission on orders
   * that were never referrals; moving it LATER orphans commission already accrued and
   * shifts which month the first statement covers. Both are legitimate before go-live
   * and neither should be casual afterwards.
   */
  commissionStartsAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD'),
});
export type ProgrammeSettings = z.infer<typeof programmeSettingsSchema>;

/** Every field optional, so an admin can change one without restating the rest. */
export const updateSettingsInputSchema = programmeSettingsSchema
  .partial()
  .refine((input) => Object.values(input).some((value) => value !== undefined), {
    message: 'nothing to change',
  });
export type UpdateSettingsInput = z.infer<typeof updateSettingsInputSchema>;

/**
 * A referral link an admin is creating for a named partner.
 *
 * `partnerName` is absent on purpose: the URL's `utm_source` comes from the partner's
 * stored slug, not from a name the client sends. A client that could supply the name
 * could attribute a link to the wrong company's traffic.
 */
export const createLinkInputSchema = z.object({
  label: z.string().trim().min(1).max(191),
  destination: z.string().url().max(2048).optional(),
  channel: channelSchema.optional(),
  placement: placementSchema.optional(),
  campaign: z.string().trim().min(1).max(191).optional(),
});
export type CreateLinkInput = z.infer<typeof createLinkInputSchema>;

export const createdLinkSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Stored as generated, so a later settings change cannot rewrite a published link. */
  url: z.string(),
  channel: z.string(),
  placement: z.string().nullable(),
});
export type CreatedLink = z.infer<typeof createdLinkSchema>;

/**
 * Builds the link exactly as the prototype's generator and the spreadsheet's column G do.
 *
 * Blank channel, campaign or destination fall back to Settings; utm_content is omitted
 * entirely when no placement is chosen.
 */
export function buildReferralUrl(input: ReferralLinkInput, settings: Settings): string {
  const source = partnerSlug(input.partnerName);
  if (!source) return '';

  const url = new URL(input.destination ?? settings.fallbackDestination);
  url.searchParams.set('utm_source', source);
  url.searchParams.set('utm_medium', input.channel ?? settings.defaultChannel);
  url.searchParams.set('utm_campaign', input.campaign ?? settings.campaign);
  if (input.placement) url.searchParams.set('utm_content', input.placement);

  return url.toString();
}
